import { reactive, markRaw } from 'vue';
import type { Component } from 'vue';
import { generateUUID } from '@/utils/strings';
import GModalConfirm from '@/components/common/dialog/GModalConfirm.vue';
import GModalInput from '@/components/common/dialog/GModalInput.vue';
import type { InputField } from '@/interfaces/common/input_fields';

type ComponentProps<T> = T extends new (...args: any) => { $props: infer P } ? P : Record<string, any>;
type ComponentEmits<T> =
  T extends new (...args: any) => { $emit: infer EmitFn }
  ? EmitFn extends (event: infer Event, ...args: any[]) => any
  ? Record<Event extends string ? Event : string, Function>
  : never
  : never;

interface ModalEntry<T extends Component = Component> {
  id: string;
  title: string;
  size?: 'normal' | 'small' | 'bigger' | 'large';
  showCloseButton?: boolean;
  showHeader?: boolean;
  component: T;
  props?: ComponentProps<T>;
  events?: ComponentEmits<T> | Record<string, Function>;
}

const modals = reactive<ModalEntry[]>([]);

function generateId() {
  return `modal-${generateUUID()}`;
}

export const modalService = {
  modals,

  open<T extends Component>(
    component: T,
    options: {
      title: string;
      size?: 'normal' | 'small' | 'bigger' | 'large';
      showCloseButton?: boolean;
      showHeader?: boolean;
      props?: ComponentProps<T>;
      events?: ComponentEmits<T> | Record<string, Function>;
    }
  ): Promise<any> {
    return new Promise((resolve) => {

      const id = generateId();

      const events = {
        ...(options.events || {}),
        close: (value?: any) => {
          resolve(value)
          modalService.close(id);
        },
      };

      modals.push({
        id,
        title: options.title,
        size: options.size,
        showCloseButton: options.showCloseButton,
        showHeader: options.showHeader,
        component: markRaw(component),
        props: options.props ?? {},
        events: events,
      });

    })
  },

  close(id: string) {
    const index = modals.findIndex(modal => modal.id === id);
    if (index !== -1) {
      modals.splice(index, 1);
    }
  },

  async confirm(title: string, message: string, options?: {
    confirmText?: string
  }): Promise<boolean> {
    return modalService.open(GModalConfirm, {
      title, size: 'small', showCloseButton: false,
      props: { message, confirmText: options?.confirmText }
    })
  },
  async input(title: string, fields: InputField[], size: 'normal' | 'small' | 'bigger' | 'large' = 'normal', disabled = false): Promise<any> {
    return modalService.open(GModalInput, {
      title, size, showCloseButton: false,
      props: { fields, disabled }
    });
  },
  getLastModal(){
    return modals[modals.length - 1];
  },

  closeAll() {
    modals.splice(0, modals.length);
  }
};